import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, TextField,
  InputAdornment, CircularProgress, Alert, Select, FormControl, InputLabel,
  Drawer, Divider, Stack, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Add as AddIcon, Close as CloseIcon, Star as StarIcon,
  StarBorder as StarBorderIcon, ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import iwalkcvService, { IWExperiencia } from '../../services/iwalkcv.service';
import api from '../../services/api';

// Tipo local para o formulário — inclui campos que o API aceita mas não constam no tipo base
interface ExperienciaForm {
  operador_id?: number;
  titulo?: string;
  descricao_curta?: string;
  descricao?: string;
  categoria_id?: number;
  ilha_id?: number;
  duracao_minutos?: number;
  capacidade_minima?: number;
  capacidade_maxima?: number;
  preco_adulto?: number;
  preco_crianca?: number;
  ponto_encontro?: string;
  politica_cancelamento?: string;
  horas_cancelamento?: number;
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  destaque?: number;
  imagem_capa_url?: string;
}

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ARCHIVED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  ARCHIVED: 'Arquivada',
};

type Operador = { id: number; nome_empresa: string };
type Categoria = { id: number; nome: string };
type Ilha = { id: number; nome: string };

const EMPTY_FORM: ExperienciaForm = {
  operador_id: undefined,
  titulo: '',
  descricao_curta: '',
  descricao: '',
  categoria_id: undefined,
  ilha_id: undefined,
  duracao_minutos: undefined,
  capacidade_minima: undefined,
  capacidade_maxima: undefined,
  preco_adulto: undefined,
  preco_crianca: undefined,
  ponto_encontro: '',
  politica_cancelamento: 'FLEXIVEL',
  horas_cancelamento: 24,
  status: 'DRAFT',
  destaque: 0,
  imagem_capa_url: '',
};

export default function ExperienciasPage() {
  const [experiencias, setExperiencias] = useState<IWExperiencia[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Listas de apoio
  const [operadores, setOperadores] = useState<Operador[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [ilhas, setIlhas] = useState<Ilha[]>([]);

  // Drawer criar/editar
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ExperienciaForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Dialog eliminar
  const [deleteTarget, setDeleteTarget] = useState<IWExperiencia | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await iwalkcvService.getExperiencias({
        status: filterStatus || undefined,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setExperiencias(res.data || []);
      setTotal(res.total ?? res.data?.length ?? 0);
    } catch {
      setError('Erro ao carregar experiências');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    iwalkcvService.getOperadores({}).then((r) => setOperadores(r.data || [])).catch(() => {});
    api.get('/iwalkcv/public/categorias').then((r) => setCategorias(r.data?.data || r.data || [])).catch(() => {});
    api.get('/iwalkcv/public/ilhas').then((r) => setIlhas(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  function openCreate() {
    setIsEditing(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setDrawerOpen(true);
  }

  function openEdit(exp: IWExperiencia) {
    setIsEditing(true);
    setEditId(exp.id);
    // mapear campos do tipo base para o form
    setForm({
      titulo: exp.titulo,
      descricao: exp.descricao,
      descricao_curta: exp.descricao_curta,
      categoria_id: exp.categoria_id,
      ilha_id: exp.ilha_id,
      duracao_minutos: exp.duracao_minutos,
      capacidade_minima: exp.capacidade_minima,
      capacidade_maxima: exp.capacidade_maxima,
      preco_adulto: exp.preco_adulto,
      preco_crianca: exp.preco_crianca,
      ponto_encontro: exp.ponto_encontro,
      politica_cancelamento: exp.politica_cancelamento,
      horas_cancelamento: exp.horas_cancelamento,
      status: exp.status,
      destaque: exp.destaque,
      imagem_capa_url: '',
    });
    setSaveError(null);
    setDrawerOpen(true);
  }

  async function handleSave() {
    if (!form.titulo) {
      setSaveError('O título é obrigatório');
      return;
    }
    if (!isEditing && !form.operador_id) {
      setSaveError('O operador é obrigatório');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (isEditing && editId) {
        await iwalkcvService.updateExperiencia(editId, form as any);
      } else {
        await iwalkcvService.createExperiencia(form);
      }
      setDrawerOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await iwalkcvService.deleteExperiencia(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.response?.data?.error || 'Erro ao eliminar');
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleStatus(exp: IWExperiencia) {
    const novoStatus = exp.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await iwalkcvService.setExperienciaStatus(exp.id, novoStatus);
      load();
    } catch {
      // silencioso
    }
  }

  function setF<K extends keyof ExperienciaForm>(key: K, value: ExperienciaForm[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  // Helper: TextField simples ligado ao form
  const tf = (
    label: string,
    key: keyof ExperienciaForm,
    opts?: { type?: string; multiline?: boolean; rows?: number }
  ) => (
    <TextField
      label={label}
      type={opts?.type || 'text'}
      value={(form as any)[key] ?? ''}
      fullWidth
      size="small"
      multiline={opts?.multiline}
      rows={opts?.rows}
      onChange={(e) =>
        setF(
          key,
          (opts?.type === 'number'
            ? e.target.value === '' ? undefined : Number(e.target.value)
            : e.target.value) as any
        )
      }
    />
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Experiências</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}
        >
          Nova Experiência
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Pesquisar..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={filterStatus}
            label="Estado"
            onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="DRAFT">Rascunho</MenuItem>
            <MenuItem value="ACTIVE">Ativa</MenuItem>
            <MenuItem value="PAUSED">Pausada</MenuItem>
            <MenuItem value="ARCHIVED">Arquivada</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {['ID', 'Título', 'Operador', 'Ilha', 'Categoria', 'Preço Adulto', 'Estado', 'Destaque', 'Ações'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {experiencias.map((exp) => (
                  <TableRow key={exp.id} hover>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{exp.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{exp.titulo}</Typography>
                    </TableCell>
                    <TableCell>{exp.nome_empresa || '—'}</TableCell>
                    <TableCell>{exp.ilha_nome || '—'}</TableCell>
                    <TableCell>{exp.categoria_nome || '—'}</TableCell>
                    <TableCell>
                      {exp.preco_adulto != null
                        ? `${Number(exp.preco_adulto).toLocaleString('pt-CV')} CVE`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[exp.status] ?? exp.status}
                        color={STATUS_COLOR[exp.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {exp.destaque
                        ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        : <StarBorderIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {(exp.status === 'ACTIVE' || exp.status === 'PAUSED') && (
                          <Tooltip title={exp.status === 'ACTIVE' ? 'Pausar' : 'Ativar'}>
                            <IconButton size="small" onClick={() => handleToggleStatus(exp)}>
                              {exp.status === 'ACTIVE'
                                ? <ToggleOnIcon fontSize="small" color="success" />
                                : <ToggleOffIcon fontSize="small" color="disabled" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(exp)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setDeleteTarget(exp); setDeleteError(null); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {experiencias.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma experiência encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
        </Paper>
      )}

      {/* ── Drawer criar / editar ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0, display: 'flex', flexDirection: 'column' } }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={2}
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Typography variant="h6" fontWeight={700}>
            {isEditing ? 'Editar Experiência' : 'Nova Experiência'}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>

        <Box p={3} sx={{ overflowY: 'auto', flex: 1 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={2.5}>

            <Typography variant="subtitle2" color="text.secondary">Dados gerais</Typography>

            {/* Operador — só obrigatório na criação */}
            {!isEditing && (
              <FormControl fullWidth size="small">
                <InputLabel>Operador *</InputLabel>
                <Select
                  value={form.operador_id ?? ''}
                  label="Operador *"
                  onChange={(e) => setF('operador_id', Number(e.target.value))}
                >
                  {operadores.map((op) => (
                    <MenuItem key={op.id} value={op.id}>{op.nome_empresa}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {tf('Título *', 'titulo')}
            {tf('Descrição curta', 'descricao_curta', { multiline: true, rows: 2 })}
            {tf('Descrição', 'descricao', { multiline: true, rows: 4 })}

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Classificação</Typography>

            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={form.categoria_id ?? ''}
                  label="Categoria"
                  onChange={(e) => setF('categoria_id', Number(e.target.value))}
                >
                  <MenuItem value="">—</MenuItem>
                  {categorias.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Ilha</InputLabel>
                <Select
                  value={form.ilha_id ?? ''}
                  label="Ilha"
                  onChange={(e) => setF('ilha_id', Number(e.target.value))}
                >
                  <MenuItem value="">—</MenuItem>
                  {ilhas.map((i) => (
                    <MenuItem key={i.id} value={i.id}>{i.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Logística</Typography>

            <Box display="flex" gap={2}>
              {tf('Duração (min)', 'duracao_minutos', { type: 'number' })}
              {tf('Capacidade mín.', 'capacidade_minima', { type: 'number' })}
              {tf('Capacidade máx.', 'capacidade_maxima', { type: 'number' })}
            </Box>
            {tf('Ponto de encontro', 'ponto_encontro')}

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Preços</Typography>

            <Box display="flex" gap={2}>
              {tf('Preço Adulto (CVE)', 'preco_adulto', { type: 'number' })}
              {tf('Preço Criança (CVE)', 'preco_crianca', { type: 'number' })}
            </Box>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Cancelamento</Typography>

            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Política</InputLabel>
                <Select
                  value={form.politica_cancelamento || 'FLEXIVEL'}
                  label="Política"
                  onChange={(e) => setF('politica_cancelamento', e.target.value)}
                >
                  <MenuItem value="FLEXIVEL">Flexível</MenuItem>
                  <MenuItem value="MODERADA">Moderada</MenuItem>
                  <MenuItem value="RIGIDA">Rígida</MenuItem>
                </Select>
              </FormControl>
              {tf('Horas cancelamento', 'horas_cancelamento', { type: 'number' })}
            </Box>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Publicação</Typography>

            <Box display="flex" gap={2} alignItems="center">
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={form.status || 'DRAFT'}
                  label="Estado"
                  onChange={(e) => setF('status', e.target.value as ExperienciaForm['status'])}
                >
                  <MenuItem value="DRAFT">Rascunho</MenuItem>
                  <MenuItem value="ACTIVE">Ativa</MenuItem>
                  <MenuItem value="PAUSED">Pausada</MenuItem>
                  <MenuItem value="ARCHIVED">Arquivada</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.destaque)}
                    onChange={(e) => setF('destaque', e.target.checked ? 1 : 0)}
                  />
                }
                label="Destaque"
                sx={{ whiteSpace: 'nowrap' }}
              />
            </Box>

            {tf('Imagem capa URL', 'imagem_capa_url')}
          </Stack>
        </Box>

        <Box px={3} py={2} borderTop="1px solid" borderColor="divider" display="flex" gap={2} justifyContent="flex-end">
          <Button onClick={() => setDrawerOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}
          >
            {saving ? 'A guardar...' : isEditing ? 'Guardar' : 'Criar'}
          </Button>
        </Box>
      </Drawer>

      {/* ── Dialog eliminar ── */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Eliminar experiência?</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error">{deleteError}</Alert>
          ) : (
            <Stack spacing={1.5}>
              <Typography>
                Tem a certeza que quer eliminar <strong>{deleteTarget?.titulo}</strong>?
              </Typography>
              {deleteTarget && deleteTarget.status !== 'DRAFT' && (
                <Alert severity="warning">
                  Esta experiência não é um rascunho. Verifique se não tem reservas activas antes de eliminar.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          {!deleteError && (
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'A eliminar...' : 'Eliminar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
